import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { LuxSolutionsLogo } from "@/components/ProjectInquiry";
import { ShieldCheck, ShieldX, Award, Lightbulb, ArrowRight } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("de-CH", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function Verify() {
  const { certId } = useParams();
  const q = useQuery({
    queryKey: ["verify", certId],
    queryFn: async () => (await api.get(`/verify/${certId}`)).data,
    retry: false,
  });
  const valid = q.data?.valid;

  return (
    <div className="min-h-screen bg-background grain flex flex-col">
      <header className="border-b hairline">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="verify-home-link">
            <Lightbulb className="h-5 w-5 text-gold" />
            <span className="font-heading font-extrabold tracking-tight">LUX</span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Lighting Academy</span>
          </Link>
          <span className="text-xs text-muted-foreground">Zertifikatsprüfung</span>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-12">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-lg">
          {q.isLoading ? (
            <div className="h-72 rounded-3xl glass animate-pulse" />
          ) : valid ? (
            <div data-testid="verify-valid" className="rounded-3xl glass border border-gold/30 p-8 sm:p-10 text-center relative overflow-hidden">
              <div className="absolute inset-0 radial-fade pointer-events-none" />
              <div className="relative">
                <div className="mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-5" style={{ background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.4)" }}>
                  <ShieldCheck className="h-8 w-8" style={{ color: "#22c55e" }} />
                </div>
                <div className="text-[11px] uppercase tracking-[0.3em]" style={{ color: "#22c55e" }}>Verifiziert · echt</div>
                <h1 className="font-heading font-extrabold text-2xl sm:text-3xl mt-3">Gültiges Abschlusszertifikat</h1>
                <p className="text-foreground/65 mt-6 text-sm">Dieses Zertifikat wurde ausgestellt für</p>
                <div className="font-heading font-extrabold text-2xl sm:text-3xl text-gold my-3">{q.data.name || "Teilnehmer:in"}</div>
                <div className="flex items-center justify-center gap-2 text-foreground/75 text-sm">
                  <Award className="h-4 w-4 text-gold" /> {q.data.course_title}
                </div>
                <div className="mt-7 flex items-center justify-center gap-8 text-sm">
                  <div>
                    <div className="font-heading font-bold">{formatDate(q.data.completed_at)}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">Abschluss</div>
                  </div>
                  <div>
                    <div className="font-mono text-gold text-sm" data-testid="verify-cert-id">{q.data.certificate_id}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">Zertifikat-Nr.</div>
                  </div>
                </div>
                <div className="mt-8 pt-5 border-t border-white/10">
                  <LuxSolutionsLogo imgClassName="h-8 w-auto mx-auto" />
                </div>
              </div>
            </div>
          ) : (
            <div data-testid="verify-invalid" className="rounded-3xl glass border border-white/10 p-8 sm:p-10 text-center">
              <div className="mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-5" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.35)" }}>
                <ShieldX className="h-8 w-8" style={{ color: "#ef4444" }} />
              </div>
              <h1 className="font-heading font-extrabold text-2xl sm:text-3xl">Zertifikat nicht gefunden</h1>
              <p className="text-sm text-muted-foreground mt-3 max-w-sm mx-auto">
                Für die Nummer <span className="font-mono text-foreground/80">{certId}</span> konnte kein gültiges Zertifikat gefunden werden. Bitte prüfen Sie den Link oder die Zertifikat-Nummer.
              </p>
              <Link to="/" className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300">
                Zur LUX Lighting Academy <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {valid && (
            <div className="text-center mt-6">
              <Link to="/" className="text-sm text-muted-foreground hover:text-gold transition-colors inline-flex items-center gap-1" data-testid="verify-academy-cta">
                Mehr über die LUX Architect Lighting Academy <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
